import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import { motion } from 'framer-motion';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import type { ILpComponentFeature5050 } from '../../declarations/contentful';
import { LPLink } from './shared/Buttons';
import Container from './shared/Container';
import { show } from './shared/motion/MotionVariants';
import Sheet from './shared/Sheet';
import { getComponentThemeColors } from './shared/styles/ComponentTheme';
import { Desc, Eyebrow, Subhead, TitleWithAccent } from './shared/Typography';

const bgColorToClass: Record<string, string> = {
  white0: 'bg-od-bg-primary',
  warmgrey100: 'bg-od-bg-secondary',
  earth100: 'bg-od-bg-secondary',
  earth200: 'bg-od-bg-tertiary',
  brandBlue600: 'bg-od-bg-accent-brand',
  warmgrey200: 'bg-od-bg-tertiary',
  warmgrey800: 'bg-od-bg-inverse-primary',
  warmgrey950: 'bg-od-bg-inverse-primary',
  neutrals100: 'bg-od-bg-inverse-primary',
};

const Feature5050Image = ({
  isArched,
  imageSrc,
  imageAlt,
}: {
  isArched: boolean;
  imageSrc: string;
  imageAlt: string;
}) => {
  return (
    <Box
      className={cn(
        'block aspect-square w-full overflow-hidden md:aspect-auto md:h-full',
        // The arched variant is a bespoke doorway shape with no design-system
        // radius token, so its corners use arbitrary em values (matching the
        // original `99em 99em 8em 8em`).
        isArched ? 'rounded-t-[99em] rounded-b-[8em]' : 'rounded-4xl',
      )}
    >
      <Image
        src={optimizeContentfulImageUrl(imageSrc, { width: 1600, quality: 80 })}
        alt={imageAlt}
        className="h-full w-full object-cover"
      />
    </Box>
  );
};

interface CtaSheetProps {
  entry: ILpComponentFeature5050;
}

const CtaSheet = ({ entry }: CtaSheetProps) => {
  const { fields } = entry;

  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const exitComplete = () => {
    ctaRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      setIsVisible(true);
    }
  };

  return (
    <>
      {fields.ctaDisplayText && (
        <LPLink
          aria-label={fields.ctaSheetLabel || 'Open dialog'}
          analyticsName={`cosmos-landing-page-feature5050-${fields?.analyticsId}`}
          ctaStyle={fields.ctaStyle}
          ctaDisplayText={fields.ctaDisplayText}
          tabIndex={0}
          onKeyDown={onKeyDown}
          ref={ctaRef}
          onClick={() => setIsVisible(true)}
        />
      )}
      <Sheet
        entry={fields.ctaSheet}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        exitComplete={exitComplete}
      />
    </>
  );
};

const RenderFeature5050 = (entry: ILpComponentFeature5050) => {
  const { fields } = entry;
  const componentTheme = getComponentThemeColors(fields.backgroundColor);
  const bgColor = componentTheme.bg ? String(componentTheme.bg) : 'white0';
  const bgClass = bgColorToClass[bgColor] || 'bg-od-bg-primary';
  const { isArched = false, imagePosition = 'Right' } = fields;

  return (
    <Container>
      <motion.div variants={show} initial="start" animate="end">
        <Box
          className={cn(
            'block rounded-3xl md:grid md:grid-cols-2 md:justify-items-center',
            bgClass,
          )}
        >
          <Col
            className={cn(
              'h-full w-full justify-end gap-6 rounded-3xl px-6 pt-16 pb-14 text-left',
              'lg:pt-29 lg:pb-27',
              imagePosition === 'Left' ? 'xl:pr-16 xl:pl-24' : 'xl:pr-24 xl:pl-16',
              imagePosition === 'Left' ? 'md:col-start-2' : 'md:col-start-1',
              'md:row-span-full',
              bgClass,
            )}
          >
            <Col className="gap-2">
              {fields.eyebrow && <Eyebrow>{fields.eyebrow}</Eyebrow>}
              {fields.title && (
                <TitleWithAccent title={fields.title} titleAccent={fields.titleAccent} />
              )}
              {fields.subhead && <Subhead>{fields.subhead}</Subhead>}
              {fields.desc && <Desc mt="$4x">{fields.desc}</Desc>}
            </Col>
            {fields.ctaDisplayText && (fields.ctaUrl || fields.ctaSheet) && (
              <Box className="block">
                {fields.ctaDisplayText && fields.ctaUrl && (
                  <LPLink
                    aria-label={`Go to page ${fields?.ctaDisplayText}`}
                    analyticsName={`cosmos-landing-page-feature5050-${fields?.analyticsId}`}
                    ctaStyle={fields.ctaStyle}
                    ctaDisplayText={fields.ctaDisplayText}
                    ctaUrl={fields.ctaUrl}
                  />
                )}
                {fields.ctaDisplayText && fields.ctaSheet && <CtaSheet entry={entry} />}
              </Box>
            )}
          </Col>

          <Box
            className={cn(
              'block w-full rounded-3xl p-4',
              imagePosition === 'Left' ? 'md:col-start-1' : 'md:col-start-2',
              'md:row-span-full',
              bgClass,
            )}
          >
            {fields.image && (
              <Feature5050Image
                isArched={isArched}
                imageSrc={fields.image.fields?.file?.url}
                imageAlt={fields.image.fields?.description || fields.image.fields?.title}
              />
            )}
          </Box>
        </Box>
      </motion.div>
    </Container>
  );
};

const Feature5050: EntryComponent<ILpComponentFeature5050> = {
  render: RenderFeature5050,
};

export default Feature5050;
